import {
    BottomSheet,
    Box,
    Button,
    Card,
    Grid,
    GridItem,
    Icon,
    Image,
    Modal,
    TextLink,
    Typography,
    useMediaQueryContext,
} from "@drone-js/core";
import { useUrlBuilder } from "apps/commons/hooks/useUrlBuilder";
import { type FunctionComponent, useCallback, useState } from "react";
import { BenefitItem } from "./BenefitItem";
import { CustomCard } from "./CustomCard";
import { PartnerMarketingLayout } from "./PartnerMarketingLayout";
import { usePartnerMarketingModelValue } from "./partnerMarketingModelAtom";

export const AllYouHaveToDo: FunctionComponent = () => {
    const {
        allYouHaveToDoViewModel: {
            allYouHaveToDoTitle,
            cards,
            orAllYouHaveToDo,
            fastTrackImport,
            fastTrackImportIs,
            alreadyListedYourProperty,
            youCanFastTrack,
            benefit,
            benefitList,
            learnMore,
            tryFastTrack,
            whatIsFastTrack,
        },
    } = usePartnerMarketingModelValue();

    const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
    const onButtonClickB = useCallback(() => setIsBottomSheetVisible(true), []);
    const onCloseB = useCallback(() => setIsBottomSheetVisible(false), []);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const onButtonClickM = useCallback(() => setIsModalVisible(true), []);
    const onCloseM = useCallback(() => setIsModalVisible(false), []);
    const mediaQuery = useMediaQueryContext();

    const urlBuilder = useUrlBuilder();

    return (
        <PartnerMarketingLayout col={10} data-element-name="partner-all-you-have-to-do-section">
            <Typography variant="heading-2xl" textAlign="center" mt={mediaQuery.m ? "64" : "48"} mb="32">
                {allYouHaveToDoTitle}
            </Typography>
            <Grid spacing="16">
                {cards.map((card, index) => (
                    <GridItem key={index + 1} lg={3} xl={3} md={3} sm={12}>
                        <CustomCard number={index + 1} text={card.text} />
                    </GridItem>
                ))}
            </Grid>
            <Typography variant="heading-default" textAlign="center" p="24">
                {orAllYouHaveToDo}
            </Typography>
            <Card
                type="non-interactive"
                variant="outlined"
                width="100%"
                px="16"
                pt="16"
                pb={mediaQuery.m ? "16" : "0"}
                mb={mediaQuery.m ? "64" : "48"}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    gap={mediaQuery.m ? "24" : "0"}
                    qM={{ flexDirection: "row" }}
                >
                    <Box gap="24" display="flex" flexDirection="column">
                        <Box
                            borderColor="feedback.generic.strong"
                            borderStyle="solid"
                            borderWidth="1"
                            borderRadius="max"
                            px="12"
                            py="8"
                            display="flex"
                            flexDirection="row"
                            gap="8"
                            width="fit-content"
                            alignItems="center"
                        >
                            <Icon id="fill.symbol.lightning2" size="20" />
                            <Typography variant="body-default-strong">{fastTrackImport}</Typography>
                        </Box>
                        <Box gap="12" display="flex" flexDirection="column">
                            <Typography variant="body-default-strong">{alreadyListedYourProperty}</Typography>
                            <Typography variant="body-default">{youCanFastTrack}</Typography>
                            <Box>
                                <TextLink
                                    hierarchy="primary"
                                    label={learnMore}
                                    textVariant="body-s-strong"
                                    onClick={mediaQuery.m ? onButtonClickM : onButtonClickB}
                                    data-element-name="fast-track-learn-more"
                                />
                            </Box>
                            <Modal
                                visible={isModalVisible}
                                onClose={onCloseM}
                                size="m"
                                actions={[
                                    {
                                        actionType: "button",
                                        hierarchy: "primary",
                                        variant: "label",
                                        label: tryFastTrack,
                                        href: urlBuilder.automaticInputUrl(),
                                    },
                                ]}
                                data-element-name="start-import-my-property"
                            >
                                <Box pb="24" gap="32" display="flex" flexDirection="column">
                                    <Box gap="12" display="flex" flexDirection="column">
                                        <Typography variant="heading-default">{whatIsFastTrack}</Typography>
                                        <Typography variant="body-default" textColor="base.subtle">
                                            {fastTrackImportIs}
                                        </Typography>
                                    </Box>
                                    <Box gap="12" display="flex" flexDirection="column">
                                        <Typography variant="heading-default">{benefit}</Typography>
                                        <Box gap="16" display="flex" flexDirection="column">
                                            {benefitList.map((benefit) => (
                                                <BenefitItem
                                                    key={benefit.benefit}
                                                    title={benefit.benefit}
                                                    description={benefit.explanation}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Modal>
                            {!mediaQuery.m && (
                                <BottomSheet
                                    height="content-fit"
                                    title={whatIsFastTrack}
                                    visible={isBottomSheetVisible}
                                    onChange={onCloseB}
                                    closeButton={{
                                        "aria-label": "Close",
                                    }}
                                >
                                    <Box py="16" gap="32" display="flex" flexDirection="column">
                                        <Box px="16">
                                            <Typography variant="body-default" textColor="base.subtle">
                                                {fastTrackImportIs}
                                            </Typography>
                                        </Box>
                                        <Box gap="12" px="16" display="flex" flexDirection="column">
                                            <Typography variant="heading-default">{benefit}</Typography>
                                            <Box gap="16" display="flex" flexDirection="column">
                                                {benefitList.map((benefit) => (
                                                    <BenefitItem
                                                        key={benefit.benefit}
                                                        title={benefit.benefit}
                                                        description={benefit.explanation}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                        <Box
                                            borderStyle="solid"
                                            borderWidth={["1", "0", "0"]}
                                            borderColor="base.subtle"
                                            pt="16"
                                        >
                                            <Box px="16">
                                                <Button
                                                    variant="label"
                                                    hierarchy="primary"
                                                    label={tryFastTrack}
                                                    href={urlBuilder.automaticInputUrl()}
                                                    width="parent-fit"
                                                    size="l"
                                                    data-element-name="start-import-my-property"
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </BottomSheet>
                            )}
                        </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                        <Image
                            alt="Agoji Move Fast and be Efficient"
                            src={
                                mediaQuery.m
                                    ? "https://cdn6.agoda.net/images/agoda-homes/partner-marketing/allyouhavetodo-section/allyouhavetodoicon.png"
                                    : "https://cdn6.agoda.net/images/agoda-homes/partner-marketing/allyouhavetodo-section/fast-track-setup.png"
                            }
                            height={mediaQuery.m ? "137px" : "100px"}
                            width={mediaQuery.m ? "125px" : "120px"}
                            objectFit="cover"
                            loading="immediate"
                        />
                    </Box>
                </Box>
            </Card>
        </PartnerMarketingLayout>
    );
};
